import { HttpClient } from "@angular/common/http";
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { OAuthStorage } from "angular-oauth2-oidc";
import { from } from "rxjs";
import { switchMap, take, tap } from "rxjs/operators";
import { AuthService } from "src/app/auth/auth.service";
import { DomainHelper } from "src/app/shared/helpers/domain.helper";
import { AppState } from "src/app/shared/state/app.state";
import { UIState } from "src/app/shared/state/ui.state";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-swagger",
  templateUrl: "./swagger.component.html",
  styleUrls: ["./swagger.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SwaggerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("swaggerContainer")
  swaggerContainer: ElementRef<HTMLDivElement>;

  constructor(
    private appState: AppState,
    private uiState: UIState,
    private authStorage: OAuthStorage,
    private authService: AuthService,
    private router: Router,
    private httpClient: HttpClient,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    const apiRegex = new RegExp("/api$");

    from(this.loadSwagger())
      .pipe(
        switchMap(() => this.httpClient.get(`${this.appState.baseApiUrl.replace(apiRegex, "")}/swagger/v1/swagger.json`)),
        take(1),
        tap((r) => {
          (r as any).servers = [];

          (r as any).servers.push({
            url: this.appState.baseApiUrl.replace(apiRegex, ""),
            description: this.appState.activeCommunity$.value.NxCode,
          });

          for (const community of this.appState.communities$.value) {
            if (community.NxCode === this.appState.activeCommunity$.value.NxCode) {
              continue;
            }

            const domain = DomainHelper.getDomain();
            let url = environment.api.replace("${api-url-variable}", `${community.NxCode}.${domain}`);

            (r as any).servers.push({ url: url.replace(apiRegex, ""), description: community.NxCode });
          }

          const ui = (window as any).SwaggerUIBundle({
            domNode: this.swaggerContainer.nativeElement,
            spec: r,
            docExpansion: "list",
            defaultModelsExpandDepth: 1,

            onComplete: () => {
              ui.preauthorizeApiKey("Bearer", `Bearer ${this.authStorage.getItem("access_token")}`);
            },
          });
        }),
      )
      .subscribe();
  }

  async ngOnInit(): Promise<void> {
    this.authService
      .hasSuperadmin()
      .pipe(
        take(1),
        tap((r) => {
          if (!r) {
            this.router.navigateByUrl("/");
            return;
          }
        }),
      )
      .subscribe();

    this.uiState.breadcrumbs$.next([
      {
        name: "Swagger",
        routerLink: "/admin/swagger",
      },
    ]);
  }

  ngOnDestroy(): void {
    this.unloadSwagger();
  }

  async loadSwagger(): Promise<void> {
    await this.loadScript("assets/swagger-ui/swagger-ui-bundle.js");
    await this.loadScript("assets/swagger-ui/swagger-ui-standalone-preset.js");
  }

  unloadSwagger(): void {
    this.unloadScript("assets/swagger-ui/swagger-ui-bundle.js");
    this.unloadScript("assets/swagger-ui/swagger-ui-standalone-preset.js");

    (window as any).SwaggerUIBundle = null;
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = this.renderer.createElement("script");
      script.src = src;
      script.type = "text/javascript";
      script.onload = () => resolve();
      script.onerror = () => reject(`Failed to load script ${src}`);
      this.renderer.appendChild(document.body, script);
    });
  }

  private unloadScript(src: string): void {
    const script = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      return;
    }
    
    this.renderer.removeChild(document.body, script);
  }
}
